/**
 * StockDetailModal — Detalle de stock de un producto: resumen, stock por bodega, Kardex.
 */
import { useState } from 'react'
import { Package, MapPin, ArrowDownCircle, ArrowUpCircle, RefreshCw, TrendingUp, TrendingDown, Printer } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useAllStock, useKardex, useWarehouseLocations, useUpdateStockLocation, type WarehouseStock, type StockMovement } from '../hooks/useInventory'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import BarcodeLabelPrint from './BarcodeLabelPrint'
import { Modal } from '@/components/ds/Modal'

interface Props {
  productId: string
  productName: string
  productSku: string
  onClose: () => void
}

const TYPE_CONFIG: Record<string, { label: string; color: string; icon: typeof ArrowDownCircle }> = {
  inbound:        { label: 'Entrada', color: 'text-success-content-soft', icon: ArrowDownCircle },
  outbound:       { label: 'Salida', color: 'text-danger-solid', icon: ArrowUpCircle },
  transfer_out:   { label: 'Transfer. salida', color: 'text-warning-solid', icon: ArrowUpCircle },
  transfer_in:    { label: 'Transfer. entrada', color: 'text-blue-500', icon: ArrowDownCircle },
  adjustment_pos: { label: 'Ajuste (+)', color: 'text-emerald-400', icon: RefreshCw },
  adjustment_neg: { label: 'Ajuste (-)', color: 'text-danger-solid', icon: RefreshCw },
}

export default function StockDetailModal({ productId, productName, productSku, onClose }: Props) {
  const locale = useTenantLocale()
  const [kardexPage, setKardexPage] = useState(1)
  const [printOpen, setPrintOpen] = useState(false)

  const { data: stockData } = useAllStock({ search: productSku, page_size: 100 })
  const { data: kardexData, isLoading: loadingKardex } = useKardex({
    product_id: productId,
    page: kardexPage,
    page_size: 15,
  })

  if (!productId) return null

  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }

  const fmtQty = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    return formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)
  }

  // Filtrar stock solo del producto seleccionado
  const productStock = (stockData?.stock || []).filter(
    (s: WarehouseStock) => s.product_id === productId
  )
  const totalQty = productStock.reduce((sum: number, s: WarehouseStock) => sum + parseFloat(s.quantity), 0)
  const totalReserved = productStock.reduce((sum: number, s: WarehouseStock) => sum + parseFloat(s.reserved_quantity), 0)
  // Consolidado de todas las bodegas: el valor es la suma de cada bodega y el
  // costo promedio sale de ese valor. Antes tomaba el promedio de la PRIMERA
  // bodega y lo multiplicaba por el stock total (OBS-M08-01).
  const totalValue = productStock.reduce(
    (sum: number, s: WarehouseStock) => sum + parseFloat(s.quantity) * parseFloat(s.avg_cost), 0)
  const avgCost = totalQty > 0 ? totalValue / totalQty : 0
  const reorderPoint = productStock.length > 0 ? parseFloat(productStock[0].reorder_point) : 0

  const movements = kardexData?.movements || []
  const kardexTotal = kardexData?.count || 0

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="xl"
        title={
          <span className="flex items-center gap-2">
            <Package size={20} className="text-primary shrink-0" />
            {productName}
          </span>
        }
        subtitle={<span className="font-mono">{productSku}</span>}
        actions={
          // Estaba junto al botón de cerrar; el encabezado del componente es
          // solo título y subtítulo, así que la acción secundaria va al pie.
          <button onClick={() => setPrintOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50">
            <Printer size={12} /> Etiqueta
          </button>
        }
      >
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <SummaryCard label="Stock Total" value={fmtQty(totalQty)} color="text-content" />
              <SummaryCard label="Reservado" value={fmtQty(totalReserved)} color="text-warning-content-soft" />
              <SummaryCard label="Disponible" value={fmtQty(totalQty - totalReserved)} color="text-primary" />
              <SummaryCard label="Costo Promedio" value={fmt(avgCost)} color="text-content-muted" />
              <SummaryCard label="Valor Total" value={fmt(totalValue)} color="text-success-content" highlight />
            </div>

            {/* Reorder point alert */}
            {reorderPoint > 0 && totalQty <= reorderPoint && (
              <div className="flex items-center gap-2 p-3 bg-warning-surface border border-warning-border rounded-lg text-xs text-warning-content">
                <TrendingDown size={14} />
                Stock bajo el punto de reorden ({fmtQty(reorderPoint)}). Considere reabastecer.
              </div>
            )}

            {/* Stock por bodega */}
            <div>
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3">Stock por Bodega</h3>
              <DataTable
                columns={[
                  {
                    key: 'warehouse_code', header: 'Bodega',
                    render: (st: WarehouseStock) => (
                      <>
                        <span className="font-medium text-content">{st.warehouse_code}</span>
                        <span className="ml-2 text-xs text-content-subtle">{st.warehouse_name}</span>
                      </>
                    ),
                  },
                  {
                    key: 'location', header: 'Ubicación',
                    render: (st: WarehouseStock) => (
                      <LocationSelector stockId={st.id} warehouseId={(st as any).warehouse_id} currentCode={(st as any).location_code} />
                    ),
                  },
                  { key: 'quantity', header: 'Cantidad', numeric: true, render: (st: WarehouseStock) => fmtQty(parseFloat(st.quantity)) },
                  { key: 'reserved_quantity', header: 'Reservado', numeric: true, render: (st: WarehouseStock) => fmtQty(parseFloat(st.reserved_quantity)) },
                  {
                    key: 'disponible', header: 'Disponible', numeric: true,
                    sortValue: (st: WarehouseStock) => parseFloat(st.quantity) - parseFloat(st.reserved_quantity),
                    render: (st: WarehouseStock) => (
                      <span className="font-medium text-primary">{fmtQty(parseFloat(st.quantity) - parseFloat(st.reserved_quantity))}</span>
                    ),
                  },
                  { key: 'avg_cost', header: 'Costo Prom.', numeric: true, render: (st: WarehouseStock) => fmt(parseFloat(st.avg_cost)) },
                  {
                    key: 'valor', header: 'Valor', numeric: true,
                    sortValue: (st: WarehouseStock) => parseFloat(st.quantity) * parseFloat(st.avg_cost),
                    render: (st: WarehouseStock) => <span className="font-medium">{fmt(parseFloat(st.quantity) * parseFloat(st.avg_cost))}</span>,
                  },
                ]}
                rows={productStock}
                rowKey={(st: WarehouseStock) => st.id}
                empty={<EmptyState icon={Package} size="sm" title="Sin stock registrado." />}
                surface={false}
                minWidth={860}
                caption="Stock del producto por bodega"
              />
            </div>

            {/* Kardex */}
            <div>
              <h3 className="text-xs font-bold text-content-subtle uppercase tracking-wider mb-3">
                Kardex — Historial de Movimientos ({kardexTotal})
              </h3>
              <DataTable
                columns={[
                  { key: 'created_at', header: 'Fecha', render: (m: StockMovement) => <span className="text-xs">{formatDate(m.created_at, locale)}</span> },
                  {
                    key: 'movement_type', header: 'Tipo',
                    render: (m: StockMovement) => {
                      const cfg = TYPE_CONFIG[m.movement_type] || TYPE_CONFIG.inbound
                      const Icon = cfg.icon
                      return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cfg.color}`}><Icon size={12} /> {cfg.label}</span>
                    },
                  },
                  { key: 'warehouse_code', header: 'Bodega', render: (m: StockMovement) => <span className="text-xs">{m.warehouse_code}</span> },
                  {
                    key: 'quantity', header: 'Cantidad', numeric: true,
                    render: (m: StockMovement) => {
                      const isOut = ['outbound', 'transfer_out', 'adjustment_neg'].includes(m.movement_type)
                      return (
                        <span className={`text-xs font-medium ${isOut ? 'text-danger-solid' : 'text-success-content-soft'}`}>
                          {isOut ? '-' : '+'}{fmtQty(m.quantity)}
                        </span>
                      )
                    },
                  },
                  { key: 'unit_cost', header: 'Costo Unit.', numeric: true, render: (m: StockMovement) => <span className="text-xs">{fmt(m.unit_cost)}</span> },
                  { key: 'total_cost', header: 'Total', numeric: true, render: (m: StockMovement) => <span className="text-xs">{fmt(m.total_cost)}</span> },
                  { key: 'reference_type', header: 'Ref.', render: (m: StockMovement) => <span className="text-xs">{m.reference_type || '—'}</span> },
                ]}
                rows={movements}
                rowKey={(m: StockMovement) => m.id}
                loading={loadingKardex}
                empty={<EmptyState icon={Package} size="sm" title="Sin movimientos." />}
                surface={false}
                minWidth={760}
                caption="Kardex del producto"
              />

              {/* Paginación Kardex */}
              {kardexTotal > 15 && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-content-subtle">
                    {Math.min((kardexPage - 1) * 15 + 1, kardexTotal)}–{Math.min(kardexPage * 15, kardexTotal)} de {kardexTotal}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => setKardexPage(p => Math.max(1, p - 1))} disabled={kardexPage === 1}
                      className="px-2 py-1 text-xs border border-divider rounded disabled:opacity-40">Ant.</button>
                    <button onClick={() => setKardexPage(p => p + 1)} disabled={kardexPage * 15 >= kardexTotal}
                      className="px-2 py-1 text-xs border border-divider rounded disabled:opacity-40">Sig.</button>
                  </div>
                </div>
              )}
            </div>
          </div>
      </Modal>

      <BarcodeLabelPrint
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        product={{ name: productName, sku: productSku }}
      />
    </>
  )
}

function LocationSelector({ stockId, warehouseId, currentCode }: { stockId: string; warehouseId: string; currentCode?: string | null }) {
  const { data } = useWarehouseLocations(warehouseId)
  const { mutate, isPending } = useUpdateStockLocation(stockId)
  const { can } = usePermission()
  // `inventory.stock` no existe en la matriz de permisos (solo warehouses,
  // movements, counts, transfers). El ajuste manual de stock crea un
  // StockMovement, así que se autoriza con inventory.movements.
  const canEdit = can('inventory.movements', 'edit')
  const locations = data?.locations || []

  if (locations.length === 0 && !currentCode) {
    return <span className="text-xs text-content-subtle">—</span>
  }

  if (!canEdit) {
    return (
      <span className="text-xs text-content-muted">
        {currentCode ? `${currentCode}` : <span className="text-content-subtle">—</span>}
      </span>
    )
  }

  return (
    <select aria-label="Ubicación en bodega"
      value={currentCode || ''}
      disabled={isPending}
      onChange={e => {
        const loc = locations.find(l => l.code === e.target.value)
        mutate(loc ? loc.id : null)
      }}
      className="px-2 py-1 text-xs border border-divider rounded bg-surface text-content-muted focus:ring-1 focus:ring-primary disabled:opacity-50 min-w-[80px]"
    >
      <option value="">Sin ubicación</option>
      {locations.map(l => (
        <option key={l.id} value={l.code}>{l.code} — {l.name}</option>
      ))}
    </select>
  )
}

function SummaryCard({ label, value, color, highlight }: { label: string; value: string; color: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? 'bg-success-surface border-emerald-100' : 'bg-surface border-divider'}`}>
      <p className="text-xs text-content-subtle font-medium">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${color}`}>{value}</p>
    </div>
  )
}
